import React, { useState } from 'react';
import { Search, Loader2, Home, MapPin } from 'lucide-react';
import api from '../api/client';
import { toast } from 'sonner';

/**
 * PostcodeAddressPicker
 *
 * Props:
 *   onSelect: (address) => void  — called with {name, line1, line2, city, postcode, phone}
 *   nameValue, phoneValue: initial values
 *   showNamePhone: whether to render Name & Phone inputs (default true)
 */
const PostcodeAddressPicker = ({ onSelect, initialName = '', initialPhone = '', showNamePhone = true }) => {
  const [postcode, setPostcode] = useState('');
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manual, setManual] = useState({ line1: '', line2: '', city: '' });
  const [provider, setProvider] = useState('');
  const [note, setNote] = useState('');

  const lookup = async () => {
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true); setAddresses([]); setSelected('');
    try {
      const res = await api.get('/postcode/lookup', { params: { postcode: pc } });
      setAddresses(res.data.addresses || []);
      setProvider(res.data.provider || '');
      setNote(res.data.note || '');
      if (!res.data.addresses?.length) {
        toast.info('No addresses found for that postcode');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Postcode lookup failed');
    } finally { setLoading(false); }
  };

  // Emit selection whenever inputs change
  const emit = (idx, ovr = {}) => {
    const a = addresses[idx];
    if (!a && !manualMode) return;
    const address = manualMode ? {
      name, line1: manual.line1, line2: manual.line2, city: manual.city,
      postcode: postcode.trim().toUpperCase(), phone
    } : {
      name: ovr.name ?? name,
      line1: a.line1, line2: a.line2 || '',
      city: a.city, postcode: a.postcode, phone: ovr.phone ?? phone
    };
    onSelect(address);
  };

  const handleSelectAddress = (idx) => {
    setSelected(String(idx));
    setManualMode(false);
    // wait a tick to have latest state
    setTimeout(() => emit(idx), 0);
  };

  return (
    <div className="space-y-3">
      {/* Postcode at top */}
      <div>
        <label className="block text-xs font-semibold text-[#12263A] mb-1">UK Postcode</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              value={postcode}
              onChange={e => setPostcode(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
              placeholder="e.g. SW1A 1AA"
              className="w-full border-2 border-[#F46A25] rounded-sm py-2 pl-10 pr-3 text-sm font-semibold focus:outline-none uppercase"
            />
          </div>
          <button type="button" onClick={lookup} disabled={loading || !postcode.trim()} className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-4 py-2 rounded-sm text-sm disabled:opacity-60 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Find address
          </button>
        </div>
      </div>

      {/* House dropdown */}
      {addresses.length > 0 && !manualMode && (
        <div>
          <label className="block text-xs font-semibold text-[#12263A] mb-1 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Select your address ({addresses.length} found)
          </label>
          <select
            value={selected}
            onChange={e => handleSelectAddress(parseInt(e.target.value, 10))}
            className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
          >
            <option value="">-- Choose your house --</option>
            {addresses.map((a, i) => (
              <option key={i} value={i}>{a.formatted}</option>
            ))}
          </select>
          <button type="button" onClick={() => { setManualMode(true); setManual({ line1: '', line2: '', city: '' }); }} className="mt-1 text-xs text-[#F46A25] hover:underline">
            Can&apos;t find your address? Enter manually
          </button>
          {provider === 'stub' && note && (
            <div className="mt-2 text-[11px] text-neutral-600 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1">
              <b>Demo mode:</b> {note}
            </div>
          )}
        </div>
      )}

      {/* Manual entry mode */}
      {manualMode && (
        <div className="space-y-2 border-l-2 border-orange-300 pl-3">
          <div>
            <label className="block text-xs font-semibold text-[#12263A] mb-1">Address line 1</label>
            <input required value={manual.line1} onChange={e => setManual(m => ({ ...m, line1: e.target.value }))} onBlur={() => emit(null)} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#12263A] mb-1">Address line 2 (optional)</label>
            <input value={manual.line2} onChange={e => setManual(m => ({ ...m, line2: e.target.value }))} onBlur={() => emit(null)} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#12263A] mb-1">City / Town</label>
            <input required value={manual.city} onChange={e => setManual(m => ({ ...m, city: e.target.value }))} onBlur={() => emit(null)} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          {addresses.length > 0 && (
            <button type="button" onClick={() => setManualMode(false)} className="text-xs text-[#F46A25] hover:underline">← Back to postcode search results</button>
          )}
        </div>
      )}

      {/* Name & phone */}
      {showNamePhone && (
        <>
          <div>
            <label className="block text-xs font-semibold text-[#12263A] mb-1">Full name</label>
            <input required value={name} onChange={e => setName(e.target.value)} onBlur={() => selected !== '' && emit(parseInt(selected, 10), { name })} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#12263A] mb-1">Phone (optional, used for WhatsApp updates)</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} onBlur={() => selected !== '' && emit(parseInt(selected, 10), { phone })} placeholder="+44 7700 900123" className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
        </>
      )}
    </div>
  );
};

export default PostcodeAddressPicker;
